<template>
  <div class="tier-group-inline">
    <div class="d-flex align-items-center mb-2">
      <span>{{ tierGroup.name }}</span>

      <CBadge
        :color="getBadge(tierGroup.status)"
        class="ml-2"
      >
        {{ tierGroup.status }}
      </CBadge>

      <small
        v-if="currentRpcRangeLabel"
        class="ml-2 text-muted"
      >
        {{ currentRpcRangeLabel }}
      </small>
      <CButton
        v-if="collapsible"
        color="link"
        size="sm"
        class="ml-2 p-0"
        @click="$emit('update:collapsed', !collapsed)"
      >
        {{ collapsed ? 'Show group' : 'Hide group' }}
      </CButton>
      <CButton
        v-if="canPause"
        color="warning"
        size="sm"
        class="ml-auto"
        :disabled="isLoading"
        @click="askStatusChange('Paused')"
      >
        Pause tier switching
      </CButton>
      <CButton
        v-else-if="canResume"
        color="success"
        size="sm"
        class="ml-auto"
        :disabled="isLoading"
        @click="askStatusChange('Active')"
      >
        Resume tier switching
      </CButton>
    </div>

    <template v-if="!collapsible || !collapsed">
      <div
        v-if="!entries.length"
        class="text-muted"
      >
        No categories in this tier group.
      </div>

      <table
        v-else
        class="table table-sm tier-group-table"
      >
        <thead>
          <tr>
            <th>Category</th>
            <th class="text-right">
              Min RPC
            </th>
            <th class="text-right">
              Max RPC
            </th>
            <th class="text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="entry in entries"
            :key="entry.categoryId"
            :class="{ 'tier-group-row--current': entry.categoryId === currentCategoryId }"
          >
            <td>
              {{ getCategoryName(entry.categoryId) }}
            </td>
            <td class="text-right">
              <CInput
                v-if="editingCategoryId === entry.categoryId"
                v-model.number="editForm.minRpc"
                type="number"
                size="sm"
                class="m-0"
                placeholder="0"
              />
              <span v-else>{{ formatRpc(entry.minRpc, '0') }}</span>
            </td>
            <td class="text-right">
              <CInput
                v-if="editingCategoryId === entry.categoryId"
                v-model.number="editForm.maxRpc"
                type="number"
                size="sm"
                class="m-0"
                placeholder="∞"
              />
              <span v-else>{{ formatRpc(entry.maxRpc, '∞') }}</span>
            </td>
            <td class="text-right">
              <template v-if="editingCategoryId === entry.categoryId">
                <CButton
                  color="primary"
                  size="sm"
                  :disabled="isLoading || !!editBoundsError"
                  @click="saveBounds(entry)"
                >
                  <CIcon name="cil-check" /> Save
                </CButton>
                <CButton
                  color="secondary"
                  size="sm"
                  class="ml-1"
                  :disabled="isLoading"
                  @click="cancelEdit"
                >
                  Cancel
                </CButton>
                <div
                  v-if="editBoundsError"
                  class="text-danger small mt-1"
                >
                  {{ editBoundsError }}
                </div>
              </template>
              <template v-else>
                <CButton
                  color="info"
                  size="sm"
                  variant="outline"
                  :disabled="isLoading"
                  @click="startEdit(entry)"
                >
                  Edit RPC
                </CButton>
                <CButton
                  color="danger"
                  size="sm"
                  variant="outline"
                  class="ml-1"
                  :disabled="isLoading"
                  @click="askRemove(entry)"
                >
                  <CIcon name="cil-trash" /> Remove
                </CButton>
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="coverageGaps.length"
        class="alert alert-info py-2 px-3 small mb-3"
      >
        <div class="mb-1">
          Uncovered RPC gaps fall to the lower-bound category:
        </div>
        <ul class="mb-0 pl-3">
          <li
            v-for="g in coverageGaps"
            :key="g.key"
          >
            RPC {{ g.label }} → {{ g.categoryName }}
          </li>
        </ul>
      </div>

      <hr>

      <div class="d-flex align-items-end flex-wrap">
        <div
          class="mr-3 flex-grow-1"
          style="min-width: 220px;"
        >
          <label class="small mb-1">Add another category</label>
          <v-select
            v-model="addForm.categoryId"
            :options="addableCategoryOptions"
            :reduce="opt => opt.value"
            :selectable="opt => !opt.disabled"
            label="label"
            placeholder="Search category..."
            :disabled="isLoading"
          />
        </div>
        <div
          class="mr-2"
          style="width: 110px;"
        >
          <label class="small mb-1">Min RPC</label>
          <CInput
            v-model.number="addForm.minRpc"
            type="number"
            size="sm"
            placeholder="0"
            class="m-0"
          />
        </div>
        <div
          class="mr-2"
          style="width: 110px;"
        >
          <label class="small mb-1">Max RPC</label>
          <CInput
            v-model.number="addForm.maxRpc"
            type="number"
            size="sm"
            placeholder="∞"
            class="m-0"
          />
        </div>
        <CButton
          color="primary"
          size="sm"
          :disabled="!canAdd || isLoading"
          @click="onAdd"
        >
          <CIcon name="cil-plus" /> Add Category
        </CButton>
      </div>

      <div
        v-if="addFormOverlapError"
        class="text-danger small mt-1"
      >
        {{ addFormOverlapError }}
      </div>

      <small class="form-text text-muted mt-2 d-block">
        At least one of Min RPC / Max RPC must be set.
      </small>
    </template>

    <CModal
      title="Remove category from tier group"
      color="danger"
      :show.sync="showRemoveModal"
    >
      <p class="mb-0">
        Remove "{{ removeCandidateName }}" from tier group "{{ tierGroup.name }}"?
      </p>
      <p
        v-if="removeIsLast"
        class="mt-2 mb-0 text-muted small"
      >
        This is the last category in the group, so the tier group will be deleted.
      </p>

      <template #footer>
        <CButton
          color="danger"
          :disabled="isLoading"
          @click="confirmRemove"
        >
          Remove
        </CButton>
        <CButton
          color="secondary"
          :disabled="isLoading"
          @click="showRemoveModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>

    <CModal
      :title="pendingStatus === 'Paused' ? 'Pause tier switching' : 'Resume tier switching'"
      :color="pendingStatus === 'Paused' ? 'warning' : 'success'"
      :show.sync="showStatusModal"
    >
      <p class="mb-0">
        {{ pendingStatus === 'Paused' ? 'Pause' : 'Resume' }} tier switching for "{{ tierGroup.name }}"?
      </p>

      <template #footer>
        <CButton
          :color="pendingStatus === 'Paused' ? 'warning' : 'success'"
          :disabled="isLoading"
          @click="confirmStatusChange"
        >
          {{ pendingStatus === 'Paused' ? 'Pause' : 'Resume' }}
        </CButton>
        <CButton
          color="secondary"
          :disabled="isLoading"
          @click="showStatusModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { getBadge } from '@/components/common/Table.vue'
import { formatRpc, formatRpcRange, rangesOverlap } from '@/utils/rpc-range'

export default {
  name: 'CategoryTierGroup',

  props: {
    tierGroup: {
      type: Object,
      required: true
    },
    currentCategoryId: {
      type: String,
      default: ''
    },
    allCategories: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    collapsible: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      editingCategoryId: null,
      editForm: {
        minRpc: null,
        maxRpc: null
      },
      addForm: {
        categoryId: null,
        minRpc: null,
        maxRpc: null
      },
      showRemoveModal: false,
      removeCandidate: null,
      showStatusModal: false,
      pendingStatus: null
    }
  },

  computed: {
    entries() {
      return this.tierGroup?.categories || []
    },

    canPause() {
      return this.tierGroup?.status === 'Active'
    },

    canResume() {
      return this.tierGroup?.status === 'Paused'
    },

    currentRpcRangeLabel() {
      if (!this.currentCategoryId) {
        return ''
      }

      const entry = this.entries.find(e => e.categoryId === this.currentCategoryId)
      if (!entry) {
        return ''
      }

      return formatRpcRange(entry.minRpc, entry.maxRpc)
    },

    addableCategoryOptions() {
      const inGroup = new Set(this.entries.map(e => e.categoryId))
      return this.allCategories
        .filter(c => !inGroup.has(c.id))
        .map(c => {
          const reasons = []
          if (c.categoryTierGroupId && c.categoryTierGroupId !== this.tierGroup.id) {
            reasons.push('in another tier group')
          }

          if (c.status !== 'Active') {
            reasons.push('not active')
          }

          if (c.isPrivate) {
            reasons.push('private')
          }

          const disabled = reasons.length > 0
          return {
            value: c.id,
            label: disabled ? `${c.name} (${reasons.join(', ')})` : c.name,
            disabled
          }
        })
    },

    sortedEntries() {
      return [...this.entries].sort((a, b) => {
        const aMin = a.minRpc != null ? Number(a.minRpc) : Number.NEGATIVE_INFINITY
        const bMin = b.minRpc != null ? Number(b.minRpc) : Number.NEGATIVE_INFINITY
        return aMin - bMin
      })
    },

    coverageGaps() {
      const sorted = this.sortedEntries
      if (!sorted.length) {
        return []
      }

      const gaps = []
      const first = sorted[0]
      if (first.minRpc != null && Number(first.minRpc) > 0) {
        gaps.push({
          key: `gap-below-${first.categoryId}`,
          label: `$0.00 – ${formatRpc(first.minRpc)}`,
          categoryName: this.getCategoryName(first.categoryId)
        })
      }

      for (let i = 0; i < sorted.length - 1; i++) {
        const cur = sorted[i]
        const next = sorted[i + 1]
        if (cur.maxRpc != null && Number(cur.maxRpc) < Number(next.minRpc)) {
          gaps.push({
            key: `gap-${cur.categoryId}-${next.categoryId}`,
            label: `${formatRpc(cur.maxRpc)} – ${formatRpc(next.minRpc)}`,
            categoryName: this.getCategoryName(cur.categoryId)
          })
        }
      }

      const last = sorted[sorted.length - 1]
      if (last.maxRpc != null) {
        gaps.push({
          key: `gap-above-${last.categoryId}`,
          label: `${formatRpc(last.maxRpc)}+`,
          categoryName: this.getCategoryName(last.categoryId)
        })
      }

      return gaps
    },

    editBoundsError() {
      if (!this.editingCategoryId) {
        return ''
      }

      const { minRpc, maxRpc } = this.editForm
      if (minRpc == null && maxRpc == null) {
        return 'At least one of Min RPC / Max RPC must be set.'
      }

      if (minRpc != null && maxRpc != null && Number(minRpc) >= Number(maxRpc)) {
        return 'Min RPC must be less than Max RPC.'
      }

      return ''
    },

    addFormOverlapError() {
      const { minRpc, maxRpc } = this.addForm
      if (minRpc == null && maxRpc == null) {
        return ''
      }

      const overlap = this.entries.find(e => rangesOverlap(minRpc, maxRpc, e.minRpc, e.maxRpc))
      if (!overlap) {
        return ''
      }

      return `RPC range overlaps with "${this.getCategoryName(overlap.categoryId)}".`
    },

    canAdd() {
      if (!this.addForm.categoryId) {
        return false
      }

      if (this.addForm.minRpc == null && this.addForm.maxRpc == null) {
        return false
      }

      return !this.addFormOverlapError
    },

    removeCandidateName() {
      return this.removeCandidate ? this.getCategoryName(this.removeCandidate.categoryId) : ''
    },

    removeIsLast() {
      return !!this.removeCandidate && this.entries.length === 1
    }
  },

  methods: {
    getBadge,
    formatRpc,

    getCategoryName(categoryId) {
      const cat = this.allCategories.find(c => c.id === categoryId)
      return cat ? cat.name : categoryId
    },

    startEdit(entry) {
      this.editingCategoryId = entry.categoryId
      this.editForm.minRpc = entry.minRpc
      this.editForm.maxRpc = entry.maxRpc
    },

    cancelEdit() {
      this.editingCategoryId = null
      this.editForm.minRpc = null
      this.editForm.maxRpc = null
    },

    saveBounds(entry) {
      if (this.editBoundsError) {
        return
      }

      this.$emit('update-rpc-bounds', {
        categoryId: entry.categoryId,
        minRpc: this.editForm.minRpc,
        maxRpc: this.editForm.maxRpc,
        done: success => {
          if (success) {
            this.cancelEdit()
          }
        }
      })
    },

    askRemove(entry) {
      this.removeCandidate = entry
      this.showRemoveModal = true
    },

    confirmRemove() {
      if (!this.removeCandidate) {
        return
      }

      this.$emit('remove-category', this.removeCandidate.categoryId)
      this.showRemoveModal = false
      this.removeCandidate = null
    },

    onAdd() {
      this.$emit('add-category', {
        categoryId: this.addForm.categoryId,
        minRpc: this.addForm.minRpc,
        maxRpc: this.addForm.maxRpc
      })
      this.addForm.categoryId = null
      this.addForm.minRpc = null
      this.addForm.maxRpc = null
    },

    askStatusChange(nextStatus) {
      this.pendingStatus = nextStatus
      this.showStatusModal = true
    },

    confirmStatusChange() {
      if (!this.pendingStatus) {
        return
      }

      this.$emit('update-status', this.pendingStatus)
      this.showStatusModal = false
      this.pendingStatus = null
    }
  }
}
</script>

<style scoped>
.tier-group-inline {
  padding-top: calc(0.375rem + 1px);
}

.tier-group-table th {
  font-size: 0.85rem;
  color: #6b7280;
}

.tier-group-row--current {
  background-color: #f0f7ff;
}
</style>
